/**
 * TierBadge — Pill tier commerciale con gradient sottile e shimmer per tier
 * a pagamento. Stile editoriale: pillola compatta con bordino e contrasto
 * tipografico forte (uppercase, tracking).
 *
 * Tier supportati: free / base / pro / business / custom (exempt internal) +
 * mapping legacy tier marketplace_* → 'business' (vivono solo pre-MOD-5).
 * 'free' è il piano gratuito (€0), un piano a sé dal 19/05/2026: fino al 21/10/2026 qui si mostrava come «Base».
 */

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { tokens } from '../../theme';

export type TierKey = 'free' | 'base' | 'pro' | 'business' | 'custom';

interface TierBadgeProps {
  tier: string | undefined;
  /** Variante 'pill' (default) o 'flag' (rettangolare con bandiera viola). */
  variant?: 'pill' | 'flag';
  size?: 'sm' | 'md';
  /** Se true, marca il tenant come 'internal exempt' (gradient ambra). */
  exempt?: boolean;
}

function normalizeTier(tier: string | undefined, exempt: boolean): TierKey {
  if (exempt) return 'custom';
  if (!tier) return 'base';
  if (tier === 'free') return 'free';
  if (tier === 'base') return 'base';
  if (tier === 'pro' || tier === 'business') return tier;
  // Legacy mappings
  if (tier === 'full') return 'business';
  if (tier.startsWith('marketplace_')) return 'pro';
  return 'base';
}

const TIER_VISUAL: Record<
  TierKey,
  { label: string; main: string; light: string; gradient: string; tooltip: string }
> = {
  free: {
    label: 'Free',
    main: tokens.color.tierFree.light,
    light: '#A1A1AA',
    gradient: `linear-gradient(135deg, #A1A1AA 0%, ${tokens.color.tierFree.light} 100%)`,
    tooltip: 'Piano gratuito (€0, per sempre): installazioni fra aziende e commesse',
  },
  base: {
    label: 'Base',
    main: tokens.color.tierFree.main,
    light: tokens.color.tierFree.light,
    gradient: `linear-gradient(135deg, ${tokens.color.tierFree.light} 0%, ${tokens.color.tierFree.main} 100%)`,
    tooltip: 'Piano Base €29/mese — gestionale essenziale',
  },
  pro: {
    label: 'Pro',
    main: tokens.color.tierPro.main,
    light: tokens.color.tierPro.light,
    gradient: `linear-gradient(135deg, ${tokens.color.tierPro.light} 0%, ${tokens.color.tierPro.main} 100%)`,
    tooltip: 'Piano Pro — gestionale completo + automazioni',
  },
  business: {
    label: 'Business',
    main: tokens.color.tierBusiness.main,
    light: tokens.color.tierBusiness.light,
    gradient: `linear-gradient(135deg, ${tokens.color.tierBusiness.light} 0%, ${tokens.color.tierBusiness.main} 100%)`,
    tooltip: 'Piano Business — AI completa + voice assistant',
  },
  custom: {
    label: 'Custom',
    main: tokens.color.tierCustom.main,
    light: tokens.color.tierCustom.light,
    gradient: `linear-gradient(135deg, ${tokens.color.tierCustom.light} 0%, ${tokens.color.tierCustom.main} 100%)`,
    tooltip: 'Tenant interno (billing exempt) — utilizzo non addebitato',
  },
};

export const TierBadge: React.FC<TierBadgeProps> = ({
  tier,
  variant = 'pill',
  size = 'sm',
  exempt = false,
}) => {
  const key = normalizeTier(tier, exempt);
  const v = TIER_VISUAL[key];
  const isPaid = key === 'business' || key === 'custom';

  const heightPx = size === 'sm' ? 22 : 26;
  const fontSize = size === 'sm' ? '0.66rem' : '0.74rem';
  const px = size === 'sm' ? 8 : 10;

  if (variant === 'flag') {
    return (
      <Tooltip title={v.tooltip} arrow placement="top">
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: heightPx + 4,
            px: 1,
            borderRadius: '4px',
            background: v.gradient,
            color: '#fff',
            fontFamily: tokens.font.body,
            fontWeight: 700,
            fontSize,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            position: 'relative',
            ...(isPaid && {
              backgroundImage: `${v.gradient}, linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)`,
              backgroundSize: '100% 100%, 200% 100%',
              animation: 'tier-shimmer 4.5s linear infinite',
            }),
          }}
        >
          {v.label}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={v.tooltip} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          height: heightPx,
          px: `${px}px`,
          borderRadius: '999px',
          background: v.gradient,
          color: '#fff',
          fontFamily: tokens.font.body,
          fontWeight: 700,
          fontSize,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          position: 'relative',
          boxShadow: isPaid
            ? `0 1px 2px ${v.main}40, 0 0 0 1px ${v.main}20`
            : `0 1px 2px ${v.main}30`,
          ...(isPaid && {
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'tier-shimmer 5s linear infinite',
              pointerEvents: 'none',
            },
          }),
        }}
      >
        {v.label}
      </Box>
    </Tooltip>
  );
};
